// GENERATED by packages/contracts/scripts/generate.ts — do not edit by hand.

import type { AbandonRequest, AnswerKeyCompletenessResponse, AssessmentCreateRequest, AssessmentResponse, AssessmentsResponse, AssistanceRequest, AssistanceResponse, AttemptStartResponse, AuthSessionResponse, BootstrapResponse, ConsentDecisionRequest, ConsentDecisionsResponse, ConsentStatementResponse, ContentDeliveryRequest, ContentDeliveryResponse, ContentPresentationRequest, ContentPresentationResponse, CourseResponse, CoursesResponse, CourseTopicsResponse, CourseUpdateRequest, DeleteAllDataRequest, DeleteAllDataResponse, DeletionPreviewResponse, ErrorResponse, ExamPrepAssessmentsResponse, ExamPrepAttemptRequest, ExamPrepAttemptResponse, ExamPrepItem, ExamPrepPlanCreateRequest, ExamPrepPlanResponse, ExamPrepSessionResponse, ExamPrepSessionSummaryResponse, ExamPrepTopicOrderRequest, ExportDownloadRequest, ExportResponse, HealthResponse, HomeworkConfidenceRequest, HomeworkConfidenceResponse, HomeworkConfirmRequest, HomeworkCorrectionRequest, HomeworkDisputeResponse, HomeworkExercisesResponse, HomeworkFeedbackChannelResponse, HomeworkFeedbackReasonResponse, HomeworkFeedbackResponse, HomeworkLabelRequest, HomeworkListResponse, HomeworkRungRequest, HomeworkTranscriptionResponse, HomeworkTriageResponse, HomeworkUploadResponse, ImportQuerystring, ImportRunsResponse, ImportUploadResponse, LearnerDeletionPreviewResponse, LearnerDeletionRequest, LearnerDeletionResponse, LoginRequest, LogoutResponse, MoodleConnectBeginResponse, MoodleConnectFinishRequest, MoodleConnectionResponse, MoodleConnectRequest, MoodleConnectResponse, MoodleSyncResponse, MyDataResponse, ObjectiveEvidenceResponse, ObjectiveStatusResponse, PractisableActivitiesResponse, ProfileResponse, ProfileUpdateRequest, ProgressSummaryResponse, ResponseSubmissionRequest, ResponseSubmissionResponse, SessionRequest, SessionResponse, SignupRequest, UsageResponse } from './types.js';
import { API_SCHEMAS } from './schemas.js';

/**
 * Every failure name the API can return, from the envelope's enum.
 *
 * Derived rather than restated: the server types its own ERROR_CODES table
 * against this, so a code that exists on one side and not the other fails
 * typecheck instead of leaving a client branch that silently stops being
 * taken (issue #14).
 */
export type ErrorCode = ErrorResponse['code'];

/** The enum as a runtime value — same source, so the two cannot disagree. */
const ERROR_CODES: ReadonlySet<string> = new Set(API_SCHEMAS.ErrorResponse.properties.code.enum);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODES.has(value);
}

/**
 * The API answered, and the answer was an error envelope.
 *
 * `message` is deliberately not carried. The envelope has one, but it is a
 * fixed server-side sentence; a UI owes its reader words of its own, and
 * forwarding the server string quietly moves that decision across the
 * boundary. What a caller gets is the `code` to branch on, the `status`,
 * and the `requestId` that names the log line where the cause actually is.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  /** `undefined` when the body was not a well-formed envelope — see parseErrorResponse. */
  readonly code: ErrorCode | undefined;
  readonly requestId: string | undefined;
  readonly details: readonly string[] | undefined;

  constructor(status: number, envelope: ErrorResponse | undefined) {
    super(`Request failed with ${String(status)} (${envelope?.code ?? "no envelope"}).`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = envelope?.code;
    this.requestId = envelope?.requestId;
    this.details = envelope?.details;
  }
}

/** Every string bound below is read from the schema, never restated here. */
function isBoundedString(value: unknown, minLength: number, maxLength: number): value is string {
  return typeof value === 'string' && value.length >= minLength && value.length <= maxLength;
}

/**
 * Refuses to let a path parameter escape the segment it was
 * interpolated into: an empty string, '.', and '..' throw; everything
 * else is passed to `encodeURIComponent`. See encodePathSegment in
 * packages/contracts/scripts/lib.ts for why encodeURIComponent alone
 * is not enough (issue #61, finding 5) — this is that function,
 * emitted verbatim so every parameterised client method shares one
 * copy rather than repeating the guard at each call site.
 */
function encodePathSegment(value: string): string {
  if (value === '' || value === '.' || value === '..') {
    throw new Error(`refusing to use ${JSON.stringify(value)} as a path segment`);
  }
  return encodeURIComponent(value);
}

/**
 * Serialises an object's own defined properties as a query string —
 * `style: form, explode: true`, OpenAPI 3.1's spelling for "each of this
 * object's properties is its own query parameter", matching how
 * scripts/lib.ts's buildOpenApiParameters describes the same parameter
 * in generated/openapi.json. A key whose value is `undefined` is
 * omitted rather than sent as the literal string `"undefined"` — a
 * field a caller left out of the object must reach the server as
 * absent, not as a value, the same absence/blank distinction
 * import-store.ts's CreateImportInput draws for `title`.
 */
function buildQuerystring(query: Readonly<Record<string, string | undefined>>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.append(key, value);
  }
  const serialised = params.toString();
  return serialised === '' ? '' : `?${serialised}`;
}

/**
 * Reads an error envelope out of a parsed response body, or returns
 * `undefined`.
 *
 * Checked against the contract rather than trusted, because this is the one
 * response body no route schema serialises on the way out and because the
 * loopback interface is shared with every other process on the machine. An
 * unrecognised `code` yields `undefined` rather than being passed through as
 * a string: a caller that branches on a code it does not know is a caller
 * acting on a claim nothing verified.
 *
 * Length matters as much as membership here. `requestId` is the one
 * server-supplied string a UI renders verbatim, and it renders inside an
 * alert region someone reads when something has already gone wrong — so an
 * unbounded one is a sentence a hostile responder gets to write there.
 * React escaping stops markup; it does not stop prose.
 */
export function parseErrorResponse(body: unknown): ErrorResponse | undefined {
  if (body === null || typeof body !== 'object') return undefined;
  const candidate = body as Record<string, unknown>;
  const { code, message, requestId, details } = candidate;
  if (!isErrorCode(code)) return undefined;
  if (!isBoundedString(message, API_SCHEMAS.ErrorResponse.properties.message.minLength, API_SCHEMAS.ErrorResponse.properties.message.maxLength)) return undefined;
  if (!isBoundedString(requestId, API_SCHEMAS.ErrorResponse.properties.requestId.minLength, API_SCHEMAS.ErrorResponse.properties.requestId.maxLength)) return undefined;
  if (details !== undefined) {
    if (!Array.isArray(details)) return undefined;
    if (details.length > API_SCHEMAS.ErrorResponse.properties.details.maxItems) return undefined;
    const bounded = details.every((entry) =>
      isBoundedString(entry, API_SCHEMAS.ErrorResponse.properties.details.items.minLength, API_SCHEMAS.ErrorResponse.properties.details.items.maxLength),
    );
    if (!bounded) return undefined;
    return { code, message, requestId, details: details as string[] };
  }
  return { code, message, requestId };
}

export interface CreateClientOptions {
  readonly baseUrl: string;
  readonly fetch?: typeof fetch;
}

export interface Client {
  health(): Promise<HealthResponse>;
  bootstrap(): Promise<BootstrapResponse>;
  profile(): Promise<ProfileResponse>;
  updateProfile(body: ProfileUpdateRequest): Promise<ProfileResponse>;
  login(body: LoginRequest): Promise<AuthSessionResponse>;
  signup(body: SignupRequest): Promise<AuthSessionResponse>;
  logout(): Promise<LogoutResponse>;
  authSession(): Promise<AuthSessionResponse>;
  consentStatement(): Promise<ConsentStatementResponse>;
  consentDecisions(): Promise<ConsentDecisionsResponse>;
  recordConsentDecision(body: ConsentDecisionRequest): Promise<ConsentDecisionsResponse>;
  deletionPreview(): Promise<DeletionPreviewResponse>;
  deleteAllData(body: DeleteAllDataRequest): Promise<DeleteAllDataResponse>;
  exportData(): Promise<ExportResponse>;
  downloadExport(body: ExportDownloadRequest): Promise<Blob>;
  getMyData(): Promise<MyDataResponse>;
  downloadMyData(): Promise<Blob>;
  previewMyDeletion(): Promise<LearnerDeletionPreviewResponse>;
  deleteMyAccount(body: LearnerDeletionRequest): Promise<LearnerDeletionResponse>;
  createSession(idempotencyKey: string, body: SessionRequest): Promise<SessionResponse>;
  getSession(id: string): Promise<SessionResponse>;
  completeSession(id: string, idempotencyKey: string): Promise<SessionResponse>;
  startAttempt(id: string, idempotencyKey: string): Promise<AttemptStartResponse>;
  createContentDelivery(idempotencyKey: string, body: ContentDeliveryRequest): Promise<ContentDeliveryResponse>;
  createContentPresentation(idempotencyKey: string, body: ContentPresentationRequest): Promise<ContentPresentationResponse>;
  requestAssistance(id: string, idempotencyKey: string, body: AssistanceRequest): Promise<AssistanceResponse>;
  submitResponse(id: string, idempotencyKey: string, body: ResponseSubmissionRequest): Promise<ResponseSubmissionResponse>;
  abandonAttempt(id: string, idempotencyKey: string, body: AbandonRequest): Promise<SessionResponse>;
  getObjectiveStatus(id: string): Promise<ObjectiveStatusResponse>;
  getObjectiveEvidence(id: string): Promise<ObjectiveEvidenceResponse>;
  getProgressSummary(): Promise<ProgressSummaryResponse>;
  getUsage(): Promise<UsageResponse>;
  getCourses(): Promise<CoursesResponse>;
  updateCourse(courseRef: string, idempotencyKey: string, body: CourseUpdateRequest): Promise<CourseResponse>;
  getMoodleConnection(): Promise<MoodleConnectionResponse>;
  connectMoodle(body: MoodleConnectRequest): Promise<MoodleConnectResponse>;
  beginMoodleConnect(): Promise<MoodleConnectBeginResponse>;
  finishMoodleConnect(body: MoodleConnectFinishRequest): Promise<MoodleConnectResponse>;
  syncMoodle(): Promise<MoodleSyncResponse>;
  disconnectMoodle(): Promise<MoodleConnectionResponse>;
  getCourseTopics(courseRef: string): Promise<CourseTopicsResponse>;
  getAssessments(): Promise<AssessmentsResponse>;
  createAssessment(courseRef: string, idempotencyKey: string, body: AssessmentCreateRequest): Promise<AssessmentResponse>;
  withdrawAssessment(assessmentId: string, idempotencyKey: string): Promise<AssessmentResponse>;
  getExamPrepAssessments(): Promise<ExamPrepAssessmentsResponse>;
  createExamPrepPlan(idempotencyKey: string, body: ExamPrepPlanCreateRequest): Promise<ExamPrepPlanResponse>;
  getExamPrepPlan(planId: string): Promise<ExamPrepPlanResponse>;
  reorderExamPrepTopics(planId: string, idempotencyKey: string, body: ExamPrepTopicOrderRequest): Promise<ExamPrepPlanResponse>;
  startExamPrepSession(planId: string, idempotencyKey: string): Promise<ExamPrepSessionResponse>;
  getExamPrepSession(sessionId: string): Promise<ExamPrepSessionResponse>;
  finishExamPrepSession(sessionId: string, idempotencyKey: string): Promise<ExamPrepSessionSummaryResponse>;
  getExamPrepItem(itemId: string): Promise<ExamPrepItem>;
  submitExamPrepAttempt(itemId: string, idempotencyKey: string, body: ExamPrepAttemptRequest): Promise<ExamPrepAttemptResponse>;
  requestExamPrepHint(itemId: string, idempotencyKey: string): Promise<ExamPrepItem>;
  declareExamPrepUnknown(itemId: string, idempotencyKey: string): Promise<ExamPrepItem>;
  getPractisableActivities(): Promise<PractisableActivitiesResponse>;
  getAnswerKeyCompleteness(): Promise<AnswerKeyCompletenessResponse>;
  createImport(body: Uint8Array | ArrayBuffer, query?: ImportQuerystring): Promise<ImportUploadResponse>;
  getImports(): Promise<ImportRunsResponse>;
  createHomework(idempotencyKey: string, body: Uint8Array | ArrayBuffer): Promise<HomeworkUploadResponse>;
  listHomework(): Promise<HomeworkListResponse>;
  homeworkExercises(): Promise<HomeworkExercisesResponse>;
  createHomeworkForExercise(courseRef: string, exerciseId: string, idempotencyKey: string, body: Uint8Array | ArrayBuffer): Promise<HomeworkUploadResponse>;
  getHomework(id: string): Promise<HomeworkUploadResponse>;
  readHomeworkAgain(id: string, idempotencyKey: string): Promise<HomeworkUploadResponse>;
  getHomeworkTranscription(pageId: string): Promise<HomeworkTranscriptionResponse>;
  getHomeworkPageImage(pageId: string): Promise<Blob>;
  createHomeworkCorrection(pageId: string, regionId: string, idempotencyKey: string, body: HomeworkCorrectionRequest): Promise<HomeworkTranscriptionResponse>;
  setHomeworkQuestionLabel(pageId: string, regionId: string, idempotencyKey: string, body: HomeworkLabelRequest): Promise<HomeworkTranscriptionResponse>;
  confirmHomeworkPage(pageId: string, idempotencyKey: string, body: HomeworkConfirmRequest): Promise<HomeworkTranscriptionResponse>;
  recordHomeworkConfidence(id: string, idempotencyKey: string, body: HomeworkConfidenceRequest): Promise<HomeworkConfidenceResponse>;
  disputeHomeworkVerdict(id: string, claimId: string, idempotencyKey: string): Promise<HomeworkDisputeResponse>;
  checkHomework(id: string, idempotencyKey: string): Promise<HomeworkFeedbackResponse>;
  getHomeworkFeedback(id: string): Promise<HomeworkFeedbackResponse>;
  pullHomeworkRung(claimId: string, idempotencyKey: string, body: HomeworkRungRequest): Promise<HomeworkFeedbackResponse>;
  triageHomework(id: string, idempotencyKey: string): Promise<HomeworkTriageResponse>;
  getHomeworkTriage(id: string): Promise<HomeworkTriageResponse>;
  getHomeworkFeedbackChannel(id: string): Promise<HomeworkFeedbackChannelResponse>;
  openHomeworkFeedbackChannel(id: string, idempotencyKey: string): Promise<HomeworkFeedbackChannelResponse>;
  showHomeworkFeedbackReason(id: string, regionId: string, idempotencyKey: string): Promise<HomeworkFeedbackReasonResponse>;
  retryHomeworkFeedback(id: string, idempotencyKey: string): Promise<HomeworkFeedbackChannelResponse>;
}

/** Plain fetch, no HTTP dependency. */
export function createClient(options: CreateClientOptions): Client {
  const { baseUrl } = options;
  const fetchImpl = options.fetch ?? fetch;

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetchImpl(`${baseUrl}${path}`, init);
    if (!response.ok) {
      throw new ApiRequestError(response.status, await readErrorEnvelope(response));
    }
    return (await response.json()) as T;
  }

  /**
   * The same request, reading the body as bytes.
   *
   * `Blob` rather than `ArrayBuffer` because the caller is a browser that
   * will hand it to `URL.createObjectURL`, and because a Blob carries its
   * own media type — so the download keeps the type the server declared
   * instead of one the client guessed from the path.
   */
  async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
    const response = await fetchImpl(`${baseUrl}${path}`, init);
    if (!response.ok) {
      throw new ApiRequestError(response.status, await readErrorEnvelope(response));
    }
    return await response.blob();
  }

  return {
    health: () => request<HealthResponse>("/health"),
    bootstrap: () => request<BootstrapResponse>("/bootstrap"),
    profile: () => request<ProfileResponse>("/profile"),
    updateProfile: (body: ProfileUpdateRequest) =>
      request<ProfileResponse>("/profile", {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    login: (body: LoginRequest) =>
      request<AuthSessionResponse>("/auth/login", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    signup: (body: SignupRequest) =>
      request<AuthSessionResponse>("/auth/signup", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    logout: () =>
      request<LogoutResponse>("/auth/logout", {
        method: 'POST',
      }),
    authSession: () => request<AuthSessionResponse>("/auth/session"),
    consentStatement: () => request<ConsentStatementResponse>("/consent/statement"),
    consentDecisions: () => request<ConsentDecisionsResponse>("/consent/decisions"),
    recordConsentDecision: (body: ConsentDecisionRequest) =>
      request<ConsentDecisionsResponse>("/consent/decisions", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    deletionPreview: () =>
      request<DeletionPreviewResponse>("/deletion-preview", {
        method: 'POST',
      }),
    deleteAllData: (body: DeleteAllDataRequest) =>
      request<DeleteAllDataResponse>("/delete-all-data", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    exportData: () =>
      request<ExportResponse>("/export", {
        method: 'POST',
      }),
    downloadExport: (body: ExportDownloadRequest) =>
      requestBlob("/export/download", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    getMyData: () => request<MyDataResponse>("/me/data"),
    downloadMyData: () => requestBlob("/me/data/download"),
    previewMyDeletion: () =>
      request<LearnerDeletionPreviewResponse>("/me/deletion/preview", {
        method: 'POST',
      }),
    deleteMyAccount: (body: LearnerDeletionRequest) =>
      request<LearnerDeletionResponse>("/me/deletion/confirm", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    createSession: (idempotencyKey: string, body: SessionRequest) =>
      request<SessionResponse>("/sessions", {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    getSession: (id: string) => request<SessionResponse>(`/sessions/${encodePathSegment(id)}`),
    completeSession: (id: string, idempotencyKey: string) =>
      request<SessionResponse>(`/sessions/${encodePathSegment(id)}/complete`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    startAttempt: (id: string, idempotencyKey: string) =>
      request<AttemptStartResponse>(`/session-items/${encodePathSegment(id)}/attempts`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    createContentDelivery: (idempotencyKey: string, body: ContentDeliveryRequest) =>
      request<ContentDeliveryResponse>("/content-deliveries", {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    createContentPresentation: (idempotencyKey: string, body: ContentPresentationRequest) =>
      request<ContentPresentationResponse>("/content-presentations", {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    requestAssistance: (id: string, idempotencyKey: string, body: AssistanceRequest) =>
      request<AssistanceResponse>(`/attempts/${encodePathSegment(id)}/assistance`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    submitResponse: (id: string, idempotencyKey: string, body: ResponseSubmissionRequest) =>
      request<ResponseSubmissionResponse>(`/attempts/${encodePathSegment(id)}/responses`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    abandonAttempt: (id: string, idempotencyKey: string, body: AbandonRequest) =>
      request<SessionResponse>(`/attempts/${encodePathSegment(id)}/abandon`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    getObjectiveStatus: (id: string) => request<ObjectiveStatusResponse>(`/objectives/${encodePathSegment(id)}/status`),
    getObjectiveEvidence: (id: string) => request<ObjectiveEvidenceResponse>(`/objectives/${encodePathSegment(id)}/evidence`),
    getProgressSummary: () => request<ProgressSummaryResponse>("/progress/summary"),
    getUsage: () => request<UsageResponse>("/usage"),
    getCourses: () => request<CoursesResponse>("/courses"),
    updateCourse: (courseRef: string, idempotencyKey: string, body: CourseUpdateRequest) =>
      request<CourseResponse>(`/courses/${encodePathSegment(courseRef)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    getMoodleConnection: () => request<MoodleConnectionResponse>("/moodle/connection"),
    connectMoodle: (body: MoodleConnectRequest) =>
      request<MoodleConnectResponse>("/moodle/connection", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    beginMoodleConnect: () =>
      request<MoodleConnectBeginResponse>("/moodle/connect/begin", {
        method: 'POST',
      }),
    finishMoodleConnect: (body: MoodleConnectFinishRequest) =>
      request<MoodleConnectResponse>("/moodle/connect/finish", {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    syncMoodle: () =>
      request<MoodleSyncResponse>("/moodle/sync", {
        method: 'POST',
      }),
    disconnectMoodle: () =>
      request<MoodleConnectionResponse>("/moodle/connection", {
        method: 'DELETE',
      }),
    getCourseTopics: (courseRef: string) => request<CourseTopicsResponse>(`/courses/${encodePathSegment(courseRef)}/topics`),
    getAssessments: () => request<AssessmentsResponse>("/assessments"),
    createAssessment: (courseRef: string, idempotencyKey: string, body: AssessmentCreateRequest) =>
      request<AssessmentResponse>(`/courses/${encodePathSegment(courseRef)}/assessments`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    withdrawAssessment: (assessmentId: string, idempotencyKey: string) =>
      request<AssessmentResponse>(`/assessments/${encodePathSegment(assessmentId)}`, {
        method: 'DELETE',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getExamPrepAssessments: () => request<ExamPrepAssessmentsResponse>("/exam-prep/assessments"),
    createExamPrepPlan: (idempotencyKey: string, body: ExamPrepPlanCreateRequest) =>
      request<ExamPrepPlanResponse>("/exam-prep/plans", {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    getExamPrepPlan: (planId: string) => request<ExamPrepPlanResponse>(`/exam-prep/plans/${encodePathSegment(planId)}`),
    reorderExamPrepTopics: (planId: string, idempotencyKey: string, body: ExamPrepTopicOrderRequest) =>
      request<ExamPrepPlanResponse>(`/exam-prep/plans/${encodePathSegment(planId)}/topic-order`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    startExamPrepSession: (planId: string, idempotencyKey: string) =>
      request<ExamPrepSessionResponse>(`/exam-prep/plans/${encodePathSegment(planId)}/sessions`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getExamPrepSession: (sessionId: string) => request<ExamPrepSessionResponse>(`/exam-prep/sessions/${encodePathSegment(sessionId)}`),
    finishExamPrepSession: (sessionId: string, idempotencyKey: string) =>
      request<ExamPrepSessionSummaryResponse>(`/exam-prep/sessions/${encodePathSegment(sessionId)}/finish`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getExamPrepItem: (itemId: string) => request<ExamPrepItem>(`/exam-prep/items/${encodePathSegment(itemId)}`),
    submitExamPrepAttempt: (itemId: string, idempotencyKey: string, body: ExamPrepAttemptRequest) =>
      request<ExamPrepAttemptResponse>(`/exam-prep/items/${encodePathSegment(itemId)}/attempts`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    requestExamPrepHint: (itemId: string, idempotencyKey: string) =>
      request<ExamPrepItem>(`/exam-prep/items/${encodePathSegment(itemId)}/hint`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    declareExamPrepUnknown: (itemId: string, idempotencyKey: string) =>
      request<ExamPrepItem>(`/exam-prep/items/${encodePathSegment(itemId)}/dont-know`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getPractisableActivities: () => request<PractisableActivitiesResponse>("/practisable-activities"),
    getAnswerKeyCompleteness: () => request<AnswerKeyCompletenessResponse>("/answer-key-completeness"),
    createImport: (body: Uint8Array | ArrayBuffer, query?: ImportQuerystring) =>
      request<ImportUploadResponse>("/imports" + buildQuerystring((query ?? {}) as Record<string, string | undefined>), {
        method: 'POST',
        headers: { 'content-type': 'application/pdf' },
        body: body as unknown as ArrayBuffer,
      }),
    getImports: () => request<ImportRunsResponse>("/imports"),
    createHomework: (idempotencyKey: string, body: Uint8Array | ArrayBuffer) =>
      request<HomeworkUploadResponse>("/homework", {
        method: 'POST',
        headers: { 'content-type': 'application/pdf', 'idempotency-key': idempotencyKey },
        body: body as unknown as ArrayBuffer,
      }),
    listHomework: () => request<HomeworkListResponse>("/homework"),
    homeworkExercises: () => request<HomeworkExercisesResponse>("/homework/exercises"),
    createHomeworkForExercise: (courseRef: string, exerciseId: string, idempotencyKey: string, body: Uint8Array | ArrayBuffer) =>
      request<HomeworkUploadResponse>(`/courses/${encodePathSegment(courseRef)}/exercises/${encodePathSegment(exerciseId)}/homework`, {
        method: 'POST',
        headers: { 'content-type': 'application/pdf', 'idempotency-key': idempotencyKey },
        body: body as unknown as ArrayBuffer,
      }),
    getHomework: (id: string) => request<HomeworkUploadResponse>(`/homework/${encodePathSegment(id)}`),
    readHomeworkAgain: (id: string, idempotencyKey: string) =>
      request<HomeworkUploadResponse>(`/homework/${encodePathSegment(id)}/read-again`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getHomeworkTranscription: (pageId: string) => request<HomeworkTranscriptionResponse>(`/homework/pages/${encodePathSegment(pageId)}/transcription`),
    getHomeworkPageImage: (pageId: string) => requestBlob(`/homework/pages/${encodePathSegment(pageId)}/image`),
    createHomeworkCorrection: (pageId: string, regionId: string, idempotencyKey: string, body: HomeworkCorrectionRequest) =>
      request<HomeworkTranscriptionResponse>(`/homework/pages/${encodePathSegment(pageId)}/regions/${encodePathSegment(regionId)}/corrections`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    setHomeworkQuestionLabel: (pageId: string, regionId: string, idempotencyKey: string, body: HomeworkLabelRequest) =>
      request<HomeworkTranscriptionResponse>(`/homework/pages/${encodePathSegment(pageId)}/regions/${encodePathSegment(regionId)}/label`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    confirmHomeworkPage: (pageId: string, idempotencyKey: string, body: HomeworkConfirmRequest) =>
      request<HomeworkTranscriptionResponse>(`/homework/pages/${encodePathSegment(pageId)}/confirm`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    recordHomeworkConfidence: (id: string, idempotencyKey: string, body: HomeworkConfidenceRequest) =>
      request<HomeworkConfidenceResponse>(`/homework/${encodePathSegment(id)}/confidence`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    disputeHomeworkVerdict: (id: string, claimId: string, idempotencyKey: string) =>
      request<HomeworkDisputeResponse>(`/homework/${encodePathSegment(id)}/claims/${encodePathSegment(claimId)}/dispute`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    checkHomework: (id: string, idempotencyKey: string) =>
      request<HomeworkFeedbackResponse>(`/homework/${encodePathSegment(id)}/check`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getHomeworkFeedback: (id: string) => request<HomeworkFeedbackResponse>(`/homework/${encodePathSegment(id)}/feedback`),
    pullHomeworkRung: (claimId: string, idempotencyKey: string, body: HomeworkRungRequest) =>
      request<HomeworkFeedbackResponse>(`/homework/claims/${encodePathSegment(claimId)}/rungs`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      }),
    triageHomework: (id: string, idempotencyKey: string) =>
      request<HomeworkTriageResponse>(`/homework/${encodePathSegment(id)}/triage`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    getHomeworkTriage: (id: string) => request<HomeworkTriageResponse>(`/homework/${encodePathSegment(id)}/triage`),
    getHomeworkFeedbackChannel: (id: string) => request<HomeworkFeedbackChannelResponse>(`/homework/${encodePathSegment(id)}/feedback-channel`),
    openHomeworkFeedbackChannel: (id: string, idempotencyKey: string) =>
      request<HomeworkFeedbackChannelResponse>(`/homework/${encodePathSegment(id)}/feedback-channel/open`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    showHomeworkFeedbackReason: (id: string, regionId: string, idempotencyKey: string) =>
      request<HomeworkFeedbackReasonResponse>(`/homework/${encodePathSegment(id)}/feedback-channel/flags/${encodePathSegment(regionId)}/reason`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
    retryHomeworkFeedback: (id: string, idempotencyKey: string) =>
      request<HomeworkFeedbackChannelResponse>(`/homework/${encodePathSegment(id)}/feedback-channel/retry`, {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
      }),
  };
}

/**
 * The error envelope from a non-2xx `Response`, or `undefined`.
 *
 * A body that is not JSON is not an envelope, and must not become a throw
 * of its own — callers rely on this never rejecting, including one that
 * calls it outside its own try block.
 *
 * Exported because the browser wrapper needs exactly this and had grown its
 * own byte-identical copy. Two implementations of one check is the shape of
 * drift this package exists to prevent, and it is no less so for being
 * inside it.
 */
export async function readErrorEnvelope(response: Response): Promise<ErrorResponse | undefined> {
  try {
    return parseErrorResponse(await response.clone().json());
  } catch {
    return undefined;
  }
}
